import React, { useEffect, useRef, useState, useCallback } from 'react';

import Paper from '@mui/material/Paper';
import type { PopoverProps, PopoverPosition } from '@mui/material/Popover';
import Slide from '@mui/material/Slide';
import { styled } from '@mui/material/styles';
import { ENV } from '@/shared/config';

type Rect = { width: number; height: number };

interface PopoverOffset {
  vertical: number;
  horizontal: number;
}

export interface Cancelable {
  clear(): void;
}

export const PopoverPaper = styled(Paper)({
  position: 'absolute',
  overflowY: 'auto',
  overflowX: 'hidden',
  // So we see the popover when it's empty.
  // It's most likely on issue on userland.
  minWidth: 16,
  minHeight: 16,
  maxWidth: 'calc(100% - 32px)',
  maxHeight: 'calc(100% - 32px)',
  // We disable the focus ring for mouse, touch and keyboard users.
  outline: 0
});

function debounce<T extends (...args: any[]) => any>(func: T, wait = 166): T & Cancelable {
  let timeout: ReturnType<typeof setTimeout>;

  function debounced(...args: Parameters<T>) {
    const later = () => {
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  }

  debounced.clear = () => {
    clearTimeout(timeout);
  };

  return debounced as T & Cancelable;
}

function resolveAnchorEl(anchorEl: Element | undefined): Element | undefined {
  return anchorEl;
}

function getTransformOriginValue(transformOrigin: PopoverOffset): string {
  return [transformOrigin.horizontal, transformOrigin.vertical]
    .map(n => (typeof n === 'number' ? `${n}px` : n))
    .join(' ');
}

function getOffsetTop(rect: Rect, vertical: number | string): number {
  if (typeof vertical === 'number') return vertical;
  if (vertical === 'center') return rect.height / 2;
  if (vertical === 'bottom') return rect.height;
  return 0; // default to 'top'
}

function getOffsetLeft(rect: Rect, horizontal: number | string): number {
  if (typeof horizontal === 'number') return horizontal;
  if (horizontal === 'center') return rect.width / 2;
  if (horizontal === 'right') return rect.width;
  return 0; // default to 'left'
}

function ownerDocument(node: Node | null | undefined): Document {
  return (node && node.ownerDocument) || document;
}

function ownerWindow(node: Node | undefined): Window {
  const doc = ownerDocument(node);
  return doc.defaultView || window;
}

type PseudoPopoverProps = Pick<
  PopoverProps,
  | 'anchorOrigin'
  | 'anchorPosition'
  | 'anchorReference'
  | 'marginThreshold'
  | 'open'
  | 'PaperProps'
  | 'transformOrigin'
  | 'transitionDuration'
  | 'TransitionProps'
> & {
  anchorEl: Element | null;
  children: React.ReactNode;
};

const defaultAnchorOrigin = {
  vertical: 'top',
  horizontal: 'left'
} as const;

const defaultTransformOrigin = {
  vertical: 'top',
  horizontal: 'left'
} as const;

const PseudoPopover = (props: PseudoPopoverProps) => {
  const {
    anchorEl,
    anchorOrigin = defaultAnchorOrigin,
    anchorPosition,
    anchorReference = 'anchorEl',
    children,
    marginThreshold: marginThresholdProp,
    open,
    PaperProps,
    transformOrigin = defaultTransformOrigin,
    TransitionProps
  } = props;

  const marginThreshold = marginThresholdProp ?? 16;

  const paperRef = useRef<HTMLDivElement>(null);
  const [isPositioned, setIsPositioned] = useState(open);

  const getTransformOrigin = useCallback(
    (elemRect: Rect): PopoverOffset => ({
      vertical: getOffsetTop(elemRect, transformOrigin.vertical),
      horizontal: getOffsetLeft(elemRect, transformOrigin.horizontal)
    }),
    [transformOrigin.horizontal, transformOrigin.vertical]
  );

  const getAnchorOffset = useCallback((): PopoverPosition => {
    if (anchorReference === 'anchorPosition') {
      if (ENV.IS_DEV_SERVER && !anchorPosition) {
        console.error(
          'You need to provide a `anchorPosition` prop when using <Popover anchorReference="anchorPosition" />.'
        );
      }
      return anchorPosition as PopoverPosition;
    }

    const resolvedAnchorEl = resolveAnchorEl(anchorEl as Element);

    // If an anchor element wasn't provided, just use the parent body element of this Popover
    const anchorElement =
      resolvedAnchorEl && resolvedAnchorEl.nodeType === 1 ? resolvedAnchorEl : ownerDocument(paperRef.current).body;

    const anchorRect = anchorElement.getBoundingClientRect();

    if (ENV.IS_DEV_SERVER) {
      const box = anchorElement.getBoundingClientRect();
      if (box.top === 0 && box.left === 0 && box.right === 0 && box.bottom === 0) {
        console.warn(
          [
            'The `anchorEl` prop provided to the component is invalid.',
            'The anchor element should be part of the document layout.',
            "Make sure the element is present in the document or that it's not display none."
          ].join('\n')
        );
      }
    }

    return {
      top: anchorRect.top + getOffsetTop(anchorRect, anchorOrigin.vertical),
      left: anchorRect.left + getOffsetLeft(anchorRect, anchorOrigin.horizontal)
    };
  }, [anchorEl, anchorOrigin.horizontal, anchorOrigin.vertical, anchorPosition, anchorReference]);

  const getPositioningStyle = useCallback(
    (element: HTMLElement) => {
      const elemRect: Rect = {
        width: element.offsetWidth,
        height: element.offsetHeight
      };

      // Get the transform origin point on the element itself
      const elemTransformOrigin = getTransformOrigin(elemRect);

      if (anchorReference === 'none') {
        return {
          top: null,
          left: null,
          transformOrigin: getTransformOriginValue(elemTransformOrigin)
        };
      }

      // Get the offset of the anchoring element
      const anchorOffset = getAnchorOffset();

      // Calculate element positioning
      let top = anchorOffset.top - elemTransformOrigin.vertical;
      let left = anchorOffset.left - elemTransformOrigin.horizontal;
      const bottom = top + elemRect.height;
      const right = left + elemRect.width;

      // Use the parent window of the anchorEl if provided
      const containerWindow = ownerWindow(resolveAnchorEl(anchorEl as Element));

      // Window thresholds taking required margin into account
      const heightThreshold = containerWindow.innerHeight - marginThreshold;
      const widthThreshold = containerWindow.innerWidth - marginThreshold;

      // Check if the vertical axis needs shifting
      if (top < marginThreshold) {
        const diff = top - marginThreshold;
        top -= diff;
        elemTransformOrigin.vertical += diff;
      } else if (bottom > heightThreshold) {
        const diff = bottom - heightThreshold;
        top -= diff;
        elemTransformOrigin.vertical += diff;
      }

      if (ENV.IS_DEV_MODE && elemRect.height > heightThreshold && elemRect.height && heightThreshold) {
        console.error(
          [
            'The popover component is too tall.',
            `Some part of it can not be seen on the screen (${elemRect.height - heightThreshold}px).`,
            'Please consider adding a `max-height` to improve the user-experience.'
          ].join('\n')
        );
      }

      // Check if the horizontal axis needs shifting
      if (left < marginThreshold) {
        const diff = left - marginThreshold;
        left -= diff;
        elemTransformOrigin.horizontal += diff;
      } else if (right > widthThreshold) {
        const diff = right - widthThreshold;
        left -= diff;
        elemTransformOrigin.horizontal += diff;
      }

      return {
        top: `${Math.round(top)}px`,
        left: `${Math.round(left)}px`,
        transformOrigin: getTransformOriginValue(elemTransformOrigin)
      };
    },
    [anchorEl, anchorReference, getAnchorOffset, getTransformOrigin, marginThreshold]
  );

  const setPositioningStyles = useCallback(() => {
    const element = paperRef.current;
    if (!element) return;

    const positioning = getPositioningStyle(element);

    if (positioning.top !== null) {
      element.style.top = positioning.top;
    }
    if (positioning.left !== null) {
      element.style.left = positioning.left;
    }
    element.style.transformOrigin = positioning.transformOrigin;
    setIsPositioned(true);
  }, [getPositioningStyle]);

  const handleEntering = () => {
    setPositioningStyles();
  };

  const handleExited = () => {
    setIsPositioned(false);
  };

  useEffect(() => {
    if (open) {
      setPositioningStyles();
    }
  });

  useEffect(() => {
    if (!open) return undefined;

    const handleResize = debounce(() => {
      setPositioningStyles();
    });

    const containerWindow = ownerWindow(resolveAnchorEl(anchorEl as Element));
    containerWindow.addEventListener('resize', handleResize);

    return () => {
      handleResize.clear();
      containerWindow.removeEventListener('resize', handleResize);
    };
  }, [anchorEl, open, setPositioningStyles]);

  return (
    <Slide
      appear
      in={open}
      timeout={750}
      onEntering={handleEntering}
      onExited={handleExited}
      unmountOnExit
      {...TransitionProps}
    >
      <PopoverPaper
        ref={paperRef}
        {...PaperProps}
        style={{
          ...PaperProps?.style,
          ...(isPositioned ? undefined : { opacity: 0 })
        }}
      >
        {children}
      </PopoverPaper>
    </Slide>
  );
};

export default PseudoPopover;
